import React, { useRef, useEffect } from "react";
import { Table, Button, Col, Row, Card } from "react-bootstrap";
import { format } from "date-fns";
import STRINGS from "../Constants/Strings";

/**
 * MEMBERS APPLICATION SEARCH — WCAG + React-Bootstrap–Compliant
 * Fully refactored to match the PersonalMembersTable structure, patterns, and conventions.
 */

export default function MembersApplicationSearch({
  applicationId,
  timeframe,
  fromDate,
  toDate,
  applicationStatus,
  applicationSearchResults = [],
  applicationSearchLoading = false,
  applicationSearchPagination,
  hasApplicationSearched = false,
  setHasApplicationSearched,
  applicationSortConfig,
  applicationSearchError = "",
  handleApplicationIdChange,
  handleApplicationKeyDown,
  handleTimeframeChange,
  handleApplicationSearch,
  setApplicationStatus,
  setApplicationSearchPagination,
  handleApplicationSort,
  handleViewMember,
  showTableOnly = false,
}) {
  /* Utility Functions */
  const formatApplicationDateTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return `${format(date, "MM/dd/yyyy")}\nat\n${format(date, "hh:mm:ss a")}`;
  };

  const getApplicationStatus = (result) => {
    if (result.status === "Inactive")
      return { status: "Inactive", className: "text-info" };
    if (result.inReviewAt)
      return { status: "In Review", className: "text-warning" };
    if (result.uploadedAt)
      return { status: "Completed", className: "text-success" };
    return { status: "In Progress", className: "text-primary" };
  };

  /* Accessible / WCAG Sort Header */
  const ApplicationSortableHeader = ({ field, label }) => {
    const isSorted = applicationSortConfig?.sortBy === field;
    const direction = applicationSortConfig?.sortDirection;

    return (
      <th scope="col" className="sortable-header">
        <button
          type="button"
          className="sortable-header__button"
          onClick={() => handleApplicationSort(field)}
          aria-sort={
            isSorted
              ? direction === "ASC"
                ? "ascending"
                : "descending"
              : "none"
          }
          aria-label={
            isSorted
              ? `${label}, sorted ${
                  direction === "ASC" ? "ascending" : "descending"
                }`
              : `${label}, activate to sort`
          }
        >
          <span className="sortable-header__label">{label}</span>
          <span className="sortable-header__icon">
            {isSorted ? (
              direction === "ASC" ? (
                <i className="fas fa-sort-up" aria-hidden="true"></i>
              ) : (
                <i className="fas fa-sort-down" aria-hidden="true"></i>
              )
            ) : (
              <i className="fas fa-sort" aria-hidden="true"></i>
            )}
          </span>
        </button>
      </th>
    );
  };

  /* Reset + Submit Handlers */
  const applicationTableHeaderRef = useRef(null);
  const applicationFormRef = useRef(null);

  const handleReset = (e) => {
    e.preventDefault();
    handleApplicationIdChange({ target: { value: "" } });
    handleTimeframeChange({ target: { value: 1 } });
    setHasApplicationSearched(false);

    if (applicationFormRef?.current) {
      applicationFormRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleApplicationSearch();
  };

  /* Scroll into view after search */
  useEffect(() => {
    if (
      hasApplicationSearched &&
      applicationTableHeaderRef.current &&
      !applicationSearchLoading
    ) {
      const rootFontSize =
        parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const stickyHeightEm = 16;
      const offset = stickyHeightEm * rootFontSize;
      const headerRect = applicationTableHeaderRef.current.getBoundingClientRect();
      const top = headerRect.top + window.scrollY - offset;

      window.scrollTo({ top, behavior: "smooth" });
    }
  }, [hasApplicationSearched, applicationSearchLoading]);

  /* Timeframe Labels */
  const timeframeLabelMap = {
    "1":
      STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
        .QUICK_TIMEFRAME_OPTION_VALUE_0,
    "7":
      STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
        .QUICK_TIMEFRAME_OPTION_VALUE_1,
    "30":
      STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
        .QUICK_TIMEFRAME_OPTION_VALUE_2,
    "90":
      STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
        .QUICK_TIMEFRAME_OPTION_VALUE_3,
    "180":
      STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
        .QUICK_TIMEFRAME_OPTION_VALUE_4,
    "365":
      STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
        .QUICK_TIMEFRAME_OPTION_VALUE_5,
  };

  const normalizedLabel = timeframeLabelMap[timeframe]
    ? timeframeLabelMap[timeframe].charAt(0).toLowerCase() +
      timeframeLabelMap[timeframe].slice(1)
    : "";

  /* Table Component */
  const renderTable = () => (
    <>
      <div className="[ row ]">
        <div className="[ col-xl-12 col-lg-12 ] [ main-container__members-column ]">
          <section
            className="branch-widget branch-widget--applications"
            aria-labelledby="personal-applications-section-title"
          >
            <Card className="branch-widget__card">
              <Card.Body>
                {/* Header */}
                <div className="branch-widget__header">
                  <div className="branch-widget__header-left">
                    <i
                      className="fas fa-file-alt branch-widget__icon"
                      aria-hidden="true"
                    />
                    <h2
                      id="personal-applications-section-title"
                      className="branch-widget__title"
                    >
                      {
                        STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.TABLE
                          .TITLE
                      }
                    </h2>
                  </div>
                </div>

                {/* Subheader */}
                <div className="branch-widget__table-header">
                  <div className="row">
                    <div className="[ col-12 ] d-flex justify-content-between align-items-center flex-wrap">
                      <span
                        ref={applicationTableHeaderRef}
                        className="branch-widget__table-title"
                        aria-live="polite"
                      >
                        {!hasApplicationSearched
                          ? ""
                          : applicationSearchResults.length > 0
                          ? `${STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.TABLE.DISPLAYING_RESULTS} ${normalizedLabel}`
                          : STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS
                              .TABLE.NO_MATCHING}
                      </span>

                      {hasApplicationSearched && (
                        <Button
                          variant="light"
                          size="sm"
                          className="branch-widget__refresh branch-widget__refresh--icon"
                          aria-label={
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS
                              .TABLE.REFRESH_ARIA_LABEL
                          }
                          onClick={handleApplicationSearch}
                        >
                          <i className="fas fa-sync" aria-hidden="true"></i>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Status Filter Buttons */}
                {setApplicationStatus && (
                  <div className="mb-3 d-flex flex-wrap gap-2">
                    {["all", "inprogress", "inreview", "completed"].map((v) => (
                      <Button
                        key={v}
                        variant={
                          applicationStatus === v ? "dark" : "outline-secondary"
                        }
                        className="mr-3"
                        size="sm"
                        onClick={() => setApplicationStatus(v)}
                      >
                        {v === "all"
                          ? "All"
                          : v === "inprogress"
                          ? "In Progress"
                          : v === "inreview"
                          ? "In Review"
                          : "Completed"}
                      </Button>
                    ))}
                  </div>
                )}

                {/* Info Banner */}
                {hasApplicationSearched && applicationSearchResults.length > 0 && (
                  <div className="branch-widget__banner branch-widget__banner--info">
                    <p className="branch-widget__banner-text">
                      {
                        STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.TABLE
                          .BANNER_INFO
                      }
                    </p>
                  </div>
                )}

                {/* Pagination - Top */}
                <PaginationBlock
                  position="top"
                  pagination={applicationSearchPagination}
                  setPagination={setApplicationSearchPagination}
                />

                {/* Table */}
                <Row>
                  <Col xs={12}>
                    <Table
                      striped
                      hover
                      size="sm"
                      responsive
                      className="branch-widget__table"
                      aria-label={
                        STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.TABLE
                          .ARIA_LABEL
                      }
                    >
                      <thead>
                        <tr>
                          <ApplicationSortableHeader
                            field="type"
                            label="PRODUCT"
                          />
                          <ApplicationSortableHeader
                            field="bundleProductId"
                            label="PRODUCT ID"
                          />
                          <ApplicationSortableHeader
                            field="id"
                            label="APPLICATION ID"
                          />
                          <ApplicationSortableHeader
                            field="applicantId"
                            label="APPLICANT ID"
                          />
                          <ApplicationSortableHeader
                            field="created"
                            label="TIME/DATE"
                          />
                          <ApplicationSortableHeader
                            field="status"
                            label="STATUS"
                          />
                          <th scope="col" aria-label="View">
                            <i
                              className="fas fa-external-link-alt"
                              aria-hidden="true"
                            />
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {applicationSearchLoading ? (
                          <TableLoading />
                        ) : hasApplicationSearched &&
                          applicationSearchResults.length > 0 ? (
                          applicationSearchResults.map((result, idx) => {
                            const statusInfo = getApplicationStatus(result);
                            return (
                              <tr key={`${result.id}-${idx}`}>
                                <td>{result.type || ""}</td>
                                <td>{result.bundleProductId || ""}</td>
                                <td>{result.id || ""}</td>
                                <td>{result.applicantId || ""}</td>
                                <td>
                                  {formatApplicationDateTime(result.created)}
                                </td>
                                <td>
                                  <span
                                    className={statusInfo.className}
                                    style={{ fontWeight: 500 }}
                                  >
                                    {statusInfo.status}
                                  </span>
                                </td>
                                <td className="branch-widget__body-link">
                                  <a
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      if (handleViewMember) {
                                        handleViewMember(
                                          result.accountId,
                                          result.id,
                                          null,
                                          { fromApplicationSearch: true }
                                        );
                                      }
                                    }}
                                    aria-label={`View application ${result.id}`}
                                  >
                                    <i
                                      className="fas fa-external-link-alt"
                                      aria-hidden="true"
                                    ></i>
                                  </a>
                                </td>
                              </tr>
                            );
                          })
                        ) : !hasApplicationSearched ? (
                          <TableEmpty message="Enter search criteria and click SEARCH to view applications." />
                        ) : null}
                      </tbody>
                    </Table>
                  </Col>
                </Row>

                {/* Pagination - Bottom */}
                {applicationSearchResults.length > 19 && (
                  <PaginationBlock
                    position="bottom"
                    pagination={applicationSearchPagination}
                    setPagination={setApplicationSearchPagination}
                  />
                )}

                {/* Bottom Banner */}
                {hasApplicationSearched && (
                  <div className="branch-widget__banner branch-widget__banner--bottom">
                    {applicationSearchResults.length > 0 ? (
                      <div className="branch-widget__banner-text">
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.TABLE
                            .BANNER_BOTTOM_REVIEW
                        }
                      </div>
                    ) : (
                      <>
                        <div className="branch-widget__banner-text">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS
                              .TABLE.BANNER_BOTTOM_NO_RESULTS1
                          }
                        </div>
                        <div className="branch-widget__banner-text">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS
                              .TABLE.BANNER_BOTTOM_NO_RESULTS2
                          }
                        </div>
                      </>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </section>
        </div>
      </div>
    </>
  );

  /* Pagination Component */
  function PaginationBlock({ position, pagination, setPagination }) {
    const totalPages = Math.max(1, pagination.totalPages || 1);

    return (
      <div
        className="branch-widget__pagination d-flex align-items-center justify-content-between"
        aria-label={`Pagination ${position}`}
      >
        {/* Left side */}
        <div className="branch-widget__pagination-left d-flex align-items-center">
          <label
            htmlFor={`app-itemsPerPage-${position}`}
            className="branch-widget__pagination-label"
          >
            {STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE.ITEMS_PER_PAGE}
          </label>

          <select
            id={`app-itemsPerPage-${position}`}
            className="branch-widget__pagination-select form-select form-select-sm"
            value={pagination.pageSize}
            onChange={(e) => {
              const newSize = parseInt(e.target.value, 10);
              setPagination((prev) => ({
                ...prev,
                pageSize: newSize,
                currentPage: 0,
              }));
            }}
          >
            <option value={20}>20</option>
            <option value={40}>40</option>
          </select>
        </div>

        {/* Right side */}
        <div className="branch-widget__pagination-right d-flex align-items-center">
          <label
            htmlFor={`app-pageSelect-${position}`}
            className="branch-widget__pagination-label"
          >
            {STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE.PAGE}
          </label>

          <select
            id={`app-pageSelect-${position}`}
            className="branch-widget__pagination-select form-select form-select-sm"
            value={pagination.currentPage + 1}
            onChange={(e) =>
              setPagination((prev) => ({
                ...prev,
                currentPage: parseInt(e.target.value, 10) - 1,
              }))
            }
          >
            {Array.from({ length: totalPages }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </select>

          <span className="branch-widget__pagination-info">
            {STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE.PAGE_OF}{" "}
            {totalPages}
          </span>

          <Button
            variant="outline-secondary"
            size="sm"
            aria-label={
              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE.PREV_PAGE
            }
            className="branch-widget__pagination-btn"
            disabled={pagination.currentPage === 0}
            onClick={() =>
              setPagination((p) => ({
                ...p,
                currentPage: p.currentPage - 1,
              }))
            }
          >
            <i className="fas fa-chevron-left" aria-hidden="true"></i>
          </Button>

          <Button
            variant="outline-secondary"
            size="sm"
            aria-label={
              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE.NEXT_PAGE
            }
            className="branch-widget__pagination-btn"
            disabled={pagination.currentPage >= totalPages - 1}
            onClick={() =>
              setPagination((p) => ({
                ...p,
                currentPage: p.currentPage + 1,
              }))
            }
          >
            <i className="fas fa-chevron-right" aria-hidden="true"></i>
          </Button>
        </div>
      </div>
    );
  }

  /* Table Loading State */
  function TableLoading() {
    return (
      <tr>
        <td colSpan={7} className="text-center text-muted py-4">
          <div
            role="status"
            aria-live="polite"
            className="branch-widget__status branch-widget__status--loading"
          >
            <i className="fas fa-spinner fa-spin me-2" aria-hidden="true"></i>
            {
              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.TABLE
                .STATUS_SEARCHING
            }
          </div>
        </td>
      </tr>
    );
  }

  /* Table Empty State */
  function TableEmpty({ message }) {
    return (
      <tr>
        <td colSpan={7} className="text-center text-muted py-4">
          <div className="branch-widget__message branch-widget__message--empty">
            <div>{message}</div>
          </div>
        </td>
      </tr>
    );
  }

  /* Render Search Form OR Table */
  if (!showTableOnly) {
    return (
      <div className="[ row ]">
        <div className="[ col-xl-12 col-lg-12 col-12 ]">
          <section
            className="branch-widget branch-widget--applications-search"
            aria-labelledby="applications-search-title"
          >
            <Card className="branch-widget__card">
              <Card.Body>
                <h2 id="applications-search-title" className="sr-only">
                  {STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM.TITLE}
                </h2>

                {/* Invisible advanced search toggle placeholder */}
                <div className="[ row ]">
                  <div className="col-12 d-flex justify-content-start">
                    <div className="[ mb-3 ]">
                      <button
                        type="button"
                        className="branch-widget__action invisible"
                        aria-hidden="true"
                        tabIndex={-1}
                      >
                        <i
                          className="fas fa-sliders-h me-1"
                          aria-hidden="true"
                        ></i>
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                            .ADVANCED_SEARCH_TOGGLE
                        }
                      </button>
                    </div>
                  </div>
                </div>

                {/* Search Form */}
                <form
                  ref={applicationFormRef}
                  onSubmit={handleSubmit}
                  noValidate
                >
                  <div className="[ row ] align-items-end g-3">
                    {/* Application ID */}
                    <div className="[ col-12 col-lg-3 ]">
                      <div className="[ mb-3 ]">
                        <label htmlFor="applicationId" className="form-label">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
                              .APPLICATION_ID_LABEL
                          }
                        </label>
                        <input
                          id="applicationId"
                          className="form-control"
                          value={applicationId || ""}
                          onChange={handleApplicationIdChange}
                          onKeyDown={handleApplicationKeyDown}
                          maxLength={5}
                          placeholder={
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
                              .APPLICATION_ID_PLACEHOLDER
                          }
                        />
                      </div>
                    </div>

                    {/* Timeframe Quick Select */}
                    <div className="[ col-12 col-lg-3 ]">
                      <div className="[ mb-3 ]">
                        <label htmlFor="timeframe" className="form-label">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
                              .QUICK_TIMEFRAME_LABEL
                          }
                        </label>
                        <select
                          id="timeframe"
                          className="form-control"
                          value={timeframe}
                          onChange={handleTimeframeChange}
                        >
                          {Object.entries(timeframeLabelMap).map(
                            ([key, label]) => (
                              <option key={key} value={key}>
                                {label}
                              </option>
                            )
                          )}
                        </select>
                      </div>
                    </div>

                    {/* From Date */}
                    <div className="[ col-12 col-lg-3 ]">
                      <div className="[ mb-3 ]">
                        <label className="form-label">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
                              .DATE_FROM_LABEL
                          }
                        </label>
                        <input
                          className="form-control"
                          value={fromDate}
                          disabled
                        />
                      </div>
                    </div>

                    {/* To Date */}
                    <div className="[ col-12 col-lg-3 ]">
                      <div className="[ mb-3 ]">
                        <label className="form-label">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
                              .DATE_TO_LABEL
                          }
                        </label>
                        <input
                          className="form-control"
                          value={toDate}
                          disabled
                        />
                      </div>
                    </div>
                  </div>

                  <div className="divider"></div>

                  {/* Error + Buttons */}
                  <div className="form-row mt-4 align-items-center justify-content-sm-end">
                    <div className="col-12 col-sm-auto mb-3 mb-sm-0">
                      {applicationSearchError && (
                        <div
                          id="application-search-error"
                          className="error-msg"
                          role="alert"
                          aria-live="assertive"
                        >
                          {applicationSearchError}
                        </div>
                      )}
                    </div>

                    <div className="col-12 col-sm-auto mb-3 mb-sm-0">
                      <button
                        type="reset"
                        className="btn btn-secondary me-2"
                        onClick={handleReset}
                      >
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
                            .RESET_BUTTON
                        }
                      </button>
                    </div>

                    <div className="col-12 col-sm-auto">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        onKeyDown={handleApplicationKeyDown}
                      >
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
                            .SEARCH_BUTTON
                        }
                      </button>
                    </div>
                  </div>
                </form>
              </Card.Body>
            </Card>
          </section>
        </div>
      </div>
    );
  }

  return renderTable();
}
